// Format date and time as DD-MMM-YYYY HH:MM AM/PM (12-hour format)
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    // Prevent infinite recursion - if start_date or end_date are also objects, convert to string first
    if (dateString.start_date && dateString.end_date) {
      // Format as date range - ensure start_date and end_date are strings, not objects
      let start = dateString.start_date;
      let end = dateString.end_date;
      
      // If start_date or end_date are objects, convert to string representation
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = start.start_date || start.end_date || JSON.stringify(start);
      }
      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = end.start_date || end.end_date || JSON.stringify(end);
      }
      
      const formattedStart = formatDateTime(String(start));
      const formattedEnd = formatDateTime(String(end));
      return `${formattedStart} to ${formattedEnd}`;
    } else {
      return 'N/A';
    }
  }
  
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    // Convert to 12-hour format
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const formattedHours = String(hours).padStart(2, '0');
    
    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return 'N/A';
  }
};

// Currency formatter with truncation to 2 decimals (no rounding)
export const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') {
    return `₹${formattedWhole}`;
  }
  return `₹${formattedWhole}.${decimalPart}`;
};

// Format status
export const formatStatus = (status) => {
  if (!status) return 'N/A';
  const statusLower = String(status).trim().toLowerCase().replace(/\s+/g, ' ');
  if (statusLower === 'success' || statusLower === 'successful') {
    return 'Complete';
  } else if (statusLower === 'approved' || statusLower === 'processed') {
    return 'Approved';
  } else if (statusLower === 'rejected' || statusLower === 'cancelled') {
    return 'Rejected';
  } else if (statusLower === 'pending') {
    return 'Pending';
  } else if (statusLower === 'in_progress' || statusLower === 'in progress' || statusLower.includes('progress')) {
    return 'In Progress';
  }
  // Replace underscores with spaces and capitalize words
  const formatted = String(status)
    .trim()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return formatted;
};

// Format date helper - DD-MMM-YYYY (e.g., 22-Nov-2025)
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let date;
    
    // If it's a date range (e.g., "2025-05-16 to 2026-05-16" or "16/05/2025 - 16/05/2026")
    if (dateString.includes(' to ') || dateString.includes(' - ')) {
      const separator = dateString.includes(' to ') ? ' to ' : ' - ';
      const [startDate, endDate] = dateString.split(separator);
      const formattedStart = formatDate(startDate.trim());
      const formattedEnd = formatDate(endDate.trim());
      return `${formattedStart} - ${formattedEnd}`;
    }
    
    // Handle DD/MM/YYYY or DD/MM/YY format
    if (dateString.includes('/') && dateString.split('/').length === 3) {
      const parts = dateString.split('/');
      const day = parts[0].padStart(2, '0');
      const monthNum = parts[1].padStart(2, '0');
      let year = parts[2];
      
      // If year is 2 digits, convert to 4 digits
      if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum > 50 ? `19${year}` : `20${year}`;
      }
      
      // Create date object to get month name
      date = new Date(`${year}-${monthNum}-${day}`);
      if (!isNaN(date.getTime())) {
        const month = monthNames[date.getMonth()];
        return `${day}-${month}-${year}`;
      }
    }
    
    // Try to parse the date
    date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'N/A';
  }
};

// Get status color for badges
export const getStatusColor = (status) => {
  const statusLower = String(status).toLowerCase();
  if (statusLower === 'success' || statusLower === 'approved' || statusLower === 'processed' || statusLower === 'successful') {
    return 'bg-green-600 text-white';
  } else if (statusLower === 'rejected' || statusLower === 'cancelled') {
    return 'bg-red-600 text-white';
  } else if (statusLower === 'in progress' || statusLower === 'in_progress') {
    return 'bg-gray-600 text-white';
  } else if (statusLower === 'pending') {
    return 'bg-yellow-600 text-white';
  }
  return 'bg-gray-600 text-white';
};

// Mask phone number
export const maskPhone = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Extract digits only
  const digits = phoneNumber.replace(/\D/g, '');
  if (digits.length < 10) return phoneNumber;
  
  // If the number starts with +91 or 91, handle it properly
  if (digits.startsWith('91') && digits.length === 12) {
    // Remove country code 91 and mask the 10-digit number
    const mobileDigits = digits.slice(2); // Remove 91
    return `+91-${mobileDigits.slice(0, 2)}-****-${mobileDigits.slice(6)}`;
  } else if (digits.length === 10) {
    // Direct 10-digit number
    return `+91-${digits.slice(0, 2)}-****-${digits.slice(6)}`;
  } else {
    // Fallback - show first 2 and last 4 digits
    return `+91-${digits.slice(0, 2)}-****-${digits.slice(-4)}`;
  }
};

// Mask account number
export const maskAccount = (accountNumber) => {
  if (!accountNumber || accountNumber.trim() === '') return 'N/A';
  const digits = accountNumber.replace(/\D/g, '');
  if (digits.length <= 4) return `******${digits}`;
  const last4 = digits.slice(-4);
  return `******${last4}`;
};

// Get current role from localStorage
export const getCurrentRole = () => {
  const authRole = localStorage.getItem('authRole');
  const roleMapping = {
    'wallet_superadmin': 'SuperAdmin',
    'wallet_finance': 'Finance', 
    'wallet_ops': 'Ops',
    'wallet_support': 'Support'
  };
  return roleMapping[authRole] || null;
};

